import { Button } from "@nwga/ui/components/button";
import { createFileRoute, Link } from "@tanstack/react-router";
import { MailCheckIcon } from "lucide-react";
import * as React from "react";
import { toast } from "sonner";
import { z } from "zod";
import { AuthCard } from "../components/auth-card.tsx";
import { env } from "../env.ts";
import { authClient } from "../lib/auth.ts";

/**
 * The step between signing up and confirming an address.
 *
 * Its own route rather than a flag on the sign-up page so that a refresh, or
 * coming back to the tab an hour later, still shows the right thing — and so
 * "resend" has somewhere to live that is not tangled with the sign-up form's
 * state.
 */
export const Route = createFileRoute("/check-email")({
	validateSearch: z.object({ email: z.string().optional() }),
	component: CheckEmailPage,
});

/** Long enough that a frustrated double-click does not spend the allowance. */
const RESEND_COOLDOWN_SECONDS = 60;

function CheckEmailPage() {
	const { email } = Route.useSearch();
	const [cooldown, setCooldown] = React.useState(0);
	const [pending, setPending] = React.useState(false);

	React.useEffect(() => {
		if (cooldown <= 0) return;
		const timer = setTimeout(() => setCooldown((seconds) => seconds - 1), 1000);
		return () => clearTimeout(timer);
	}, [cooldown]);

	const resend = async () => {
		if (email === undefined) return;
		setPending(true);

		const result = await authClient.sendVerificationEmail({
			email,
			callbackURL: `${env.webUrl}/verify-email`,
		});

		setPending(false);
		setCooldown(RESEND_COOLDOWN_SECONDS);

		if (result.error !== null && result.error !== undefined) {
			// The server rate-limits this too; the message tells them to wait rather
			// than leaving a dead button.
			toast.error(
				result.error.message ?? "Could not send another email just yet.",
			);
			return;
		}

		toast.success("Sent", { description: "It should arrive within a minute." });
	};

	return (
		<AuthCard
			title="Check your email"
			description={
				email === undefined
					? "We've sent you a link to confirm your address."
					: `We've sent a link to ${email}. Click it to finish setting up your account.`
			}
			footer={
				<>
					Wrong address?{" "}
					<Link
						to="/sign-up"
						className="font-medium text-foreground underline-offset-4 hover:underline"
					>
						Sign up again
					</Link>
				</>
			}
		>
			<div className="space-y-4">
				<div className="flex items-center gap-3 rounded-lg border border-dashed p-4">
					<MailCheckIcon
						className="size-5 shrink-0 text-muted-foreground"
						aria-hidden="true"
					/>
					<p className="text-muted-foreground text-sm">
						The link expires in one hour. If it isn't there, check your spam
						folder.
					</p>
				</div>

				{email !== undefined && (
					<Button
						variant="outline"
						className="w-full"
						disabled={pending || cooldown > 0}
						onClick={() => void resend()}
					>
						{pending
							? "Sending…"
							: cooldown > 0
								? `Resend in ${cooldown}s`
								: "Resend the email"}
					</Button>
				)}
			</div>
		</AuthCard>
	);
}
